import React, { useCallback, useMemo, useState, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { MagicCookie, Settlement, Stone, NotFound } from '@app/shared/ui/molecules/Common';
import { PandoraBankSection, ShopModal } from '@app/shared/ui/organisms/Shop';
import { LoadingSpinner, ScrollArea } from '@app/shared/ui/atoms/Common';
import { InviteLinkWithVersionSection } from '@app/shared/ui/organisms/Common';
import { ShopSection } from '@app/shared/ui/molecules/Shop';
import { useGetCurrentUserQuery } from '@app/store/slices/users/usersApi';
import { useGetEnergyStoneInfo } from '@app/shared/hooks/useGetEnergyStoneInfo';
import { useGetSettlementsQuery } from '@app/store/slices/settlements/settlementsApi';
import { useDisplayError } from '@app/shared/hooks/useDisplayError';
import {
  telegramShareInviteMessageWithInitialReward,
  telegramVibrate,
} from '@app/shared/utils/common';
import { Page } from '@app/routes';
import { ShopModalType } from '@app/shared/types/pages/shop';
import { MAGIC_COOKIES } from '@app/shared/constants/cookiesShop';
import { usePreloadImages } from '@app/shared/hooks/usePreloadImages';
import { SHOP_PRELOAD_IMAGES } from '@app/assets';
import { SettlementModal } from '@app/shared/ui/organisms/Home';
import { useSettlementModal } from '@app/shared/hooks/useSettlementModal';

const CoverflowCarousel = lazy(() => import('@app/shared/ui/atoms/Common/CoverflowCarousel'));

interface ModalState {
  type: ShopModalType;
  isOpen: boolean;
}

interface ShopSectionConfig {
  key: ShopModalType;
  title: string;
  renderContent: () => React.JSX.Element;
}

export default function ShopPage() {
  const { t } = useTranslation();
  const {
    settlementModalState,
    handleSettlementModalOpen,
    handleSettlementModalClose,
    toggleIsBoosting,
    handlePurchaseSettlement,
    handleSettlementBoost,
    isPurchasingSettlement,
    isBoostingSettlement,
  } = useSettlementModal();
  const { data: user } = useGetCurrentUserQuery();
  const { energyStones, isStonesLoading, isStonesError } = useGetEnergyStoneInfo();
  const isImagesLoading = usePreloadImages(SHOP_PRELOAD_IMAGES);
  const {
    data: settlements,
    isLoading: isSettlementsLoading,
    isError: isSettlementsError,
  } = useGetSettlementsQuery();

  const [modalState, setModalState] = useState<ModalState>({
    type: 'magic-cookies',
    isOpen: false,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      const pageEl = document.querySelector('.absolute.inset-0');
      if (pageEl) {
        (pageEl as HTMLElement).style.opacity = '1';
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const failedToLoadStonesMsg = t('errors.failed_to_load_stones');
  useDisplayError(isStonesError, failedToLoadStonesMsg);
  const failedToLoadSettlementsMsg = t('errors.failed_to_load_settlements');
  useDisplayError(isSettlementsError, failedToLoadSettlementsMsg);

  const handleModalOpen = (type: ShopModalType) => {
    telegramVibrate();
    setModalState({ type, isOpen: true });
  };

  const handleModalClose = () => {
    setModalState((prevState) => ({ ...prevState, isOpen: false }));
  };

  const renderMagicCookies = useCallback(
    () => (
      <CoverflowCarousel slidesPerView={3}>
        {(SwiperSlide) =>
          MAGIC_COOKIES.map((cookie) => (
            <SwiperSlide key={cookie.price} className="my-3">
              <Link to={Page.COOKIES_SHOP}>
                <MagicCookie magicCookie={cookie} />
              </Link>
            </SwiperSlide>
          ))
        }
      </CoverflowCarousel>
    ),
    []
  );

  const renderEnergyStones = useCallback(
    () =>
      energyStones.length ? (
        <CoverflowCarousel slidesPerView={3}>
          {(SwiperSlide) =>
            energyStones.map((stone) => (
              <SwiperSlide preventClicksPropagation={true} key={stone.id} className="my-3">
                <Link to={`${Page.STONE_ABILITIES}?stone=${String(stone.id)}`}>
                  <Stone
                    nodeLevel={stone.nodeLevel}
                    image={stone.imageUrl}
                    title={stone.name}
                    imageClassName="w-[147px]"
                    className="grid! grid-rows-[1fr_32px]"
                    titleClassName={`${stone.cardColor} text-foreground rounded-xl py-1 px-2 w-fit mt-2 ml-2`}
                    price={stone.price}
                    isPurchased={stone.isPurchased}
                  />
                </Link>
              </SwiperSlide>
            ))
          }
        </CoverflowCarousel>
      ) : (
        <NotFound
          message={failedToLoadStonesMsg}
          displayPanda={false}
          withLightBlueBg
          containerClassName="h-fit my-3"
        />
      ),
    [energyStones]
  );

  const renderSettlements = useCallback(
    () =>
      settlements?.length ? (
        <CoverflowCarousel slidesPerView={3} spaceBetween={-10}>
          {(SwiperSlide) =>
            settlements.map((settlement) => (
              <SwiperSlide key={settlement.id} className="my-3">
                <Settlement
                  settlement={settlement}
                  onClick={handleSettlementModalOpen}
                  imageUrl={settlement.imageUrl}
                  withoutBackground
                />
              </SwiperSlide>
            ))
          }
        </CoverflowCarousel>
      ) : (
        <NotFound
          message={failedToLoadSettlementsMsg}
          displayPanda={false}
          withLightBlueBg
          containerClassName="h-fit my-3"
        />
      ),
    [settlements]
  );

  const shopSections = useMemo<ShopSectionConfig[]>(
    () => [
      { key: 'magic-cookies', title: t('shop.magic_cookies'), renderContent: renderMagicCookies },
      { key: 'energy-stones', title: t('shop.energy_stones'), renderContent: renderEnergyStones },
      { key: 'settlements', title: t('shop.settlements'), renderContent: renderSettlements },
    ],
    [t, renderMagicCookies, renderEnergyStones, renderSettlements]
  );

  if (isStonesLoading || isSettlementsLoading || isImagesLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ScrollArea className="gap-6">
      {shopSections.map(({ key, title, renderContent }) => (
        <ShopSection
          key={key}
          title={title}
          onOpenModal={() => {
            handleModalOpen(key);
          }}
        >
          {renderContent()}
        </ShopSection>
      ))}
      <PandoraBankSection
        onOpenModal={() => {
          handleModalOpen('pandora-bank');
        }}
      />

      {user && (
        <div className="px-4">
          <InviteLinkWithVersionSection
            onClick={() => {
              telegramShareInviteMessageWithInitialReward(user, t);
            }}
          />
        </div>
      )}

      <ShopModal
        type={modalState.type}
        isOpen={modalState.isOpen}
        onOpenChange={handleModalClose}
      />

      {settlementModalState.settlement && (
        <SettlementModal
          isOpen={settlementModalState.isOpen}
          onOpenChange={handleSettlementModalClose}
          settlement={settlementModalState.settlement}
          actionType={settlementModalState.action}
          onBoostBtnClick={handleSettlementBoost}
          onBuyBtnClick={handlePurchaseSettlement}
          isBoostBtnDisabled={isBoostingSettlement}
          isBuyBtnDisabled={isPurchasingSettlement}
          isBoosting={settlementModalState.isBoosting}
          toggleIsBoosting={toggleIsBoosting}
        />
      )}
    </ScrollArea>
  );
}
